import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../../../components/feature/PageLayout';
import PageHero from '../../../components/feature/PageHero';
import AnnouncementBuilder, { BuilderData } from '../../../components/feature/AnnouncementBuilder';
import { submitLocalForm } from '../../../lib/localForms';

export default function BaptismFormPage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isCertificateRequest, setIsCertificateRequest] = useState(false);
  const [conductedBy, setConductedBy] = useState('');
  const [conductedByOther, setConductedByOther] = useState('');
  const [locationVal, setLocationVal] = useState('');
  const [locationOther, setLocationOther] = useState('');
  const [preferredDate, setPreferredDate] = useState('');
  const [preferredTime, setPreferredTime] = useState('');
  const [builderData, setBuilderData] = useState<BuilderData>({ photoDataUrl: '', customMessage: '', imagePosition: 'center', themeIndex: 0, hasPhoto: false });
  const [posterMode, setPosterMode] = useState<'builder' | 'upload'>('builder');
  const [customPosterFile, setCustomPosterFile] = useState<File | null>(null);

  // Track child name for poster preview
  const [childFirstName, setChildFirstName] = useState('');
  const [childLastName, setChildLastName] = useState('');
  const [fatherFirstName, setFatherFirstName] = useState('');
  const [motherFirstName, setMotherFirstName] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    const urlEncodedData = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== 'conducted_by' && key !== 'location') {
        urlEncodedData.append(key, value.toString());
      }
    });
    if (conductedBy === 'Other') {
      urlEncodedData.append('conducted_by', conductedByOther || 'Other');
    } else {
      urlEncodedData.append('conducted_by', conductedBy);
    }
    if (locationVal === 'Other') {
      urlEncodedData.append('location', locationOther || 'Other');
    } else {
      urlEncodedData.append('location', locationVal);
    }
    if (!isCertificateRequest) {
      if (preferredDate) urlEncodedData.append('preferred_date', preferredDate);
      if (preferredTime) urlEncodedData.append('preferred_time', preferredTime);
      urlEncodedData.append('posterOption', posterMode);
      if (posterMode === 'upload') {
        urlEncodedData.append('customPoster', customPosterFile ? 'Uncollectable' : 'Not provided');
      } else {
        if (builderData.hasPhoto) urlEncodedData.append('announcementPhoto', 'Uncollectable');
        if (builderData.customMessage) urlEncodedData.append('announcementMessage', builderData.customMessage);
      }
    }

    try {
      const response = await submitLocalForm('Baptism Form', urlEncodedData);

      if (response.ok) {
        setSubmitStatus('success');
        form.reset();
        setIsCertificateRequest(false);
        setConductedBy('');
        setConductedByOther('');
        setLocationVal('');
        setLocationOther('');
        setPreferredDate('');
        setPreferredTime('');
        setBuilderData({ photoDataUrl: '', customMessage: '', imagePosition: 'center', themeIndex: 0, hasPhoto: false });
        setCustomPosterFile(null);
        setPosterMode('builder');
        setChildFirstName('');
        setChildLastName('');
        setFatherFirstName('');
        setMotherFirstName('');
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageLayout>
      <PageHero
        title="Baptism Form"
        subtitle="Request baptism service or certificate copy"
      />

      <div className="max-w-4xl mx-auto px-6 py-16">
        {/* Inspirational Verse */}
        <div className="mb-12 text-center">
          <p className="text-lg text-gray-700 italic font-serif">
            "Go therefore and make disciples of all nations, baptizing them in the name of the Father and of the Son and of the Holy Spirit"
          </p>
          <p className="text-sm text-gray-500 mt-2">— Matthew 28:19</p>
        </div>

        <form 
          id="baptism-form"
          data-local-form
          onSubmit={handleSubmit}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 space-y-8"
        >
          {/* Certificate Request Option */}
          <div className="pb-6 border-b border-gray-200">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="certificate_request_only"
                checked={isCertificateRequest}
                onChange={(e) => setIsCertificateRequest(e.target.checked)}
                className="mt-1 w-5 h-5 text-burgundy-700 rounded focus:ring-2 focus:ring-gold-500"
              />
              <div>
                <span className="text-base font-medium text-gray-900">
                  I am requesting a copy of baptism certificate only
                </span>
                <p className="text-sm text-gray-600 mt-1">
                  Check this box if you only need a certificate copy. You'll only need to fill the underlined fields below.
                </p>
              </div>
            </label>
          </div>

          {/* Child Information */}
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <i className="ri-user-line text-gold-600"></i>
              Child Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className={isCertificateRequest ? 'md:col-span-2' : ''}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Child First Name <span className="text-red-500">*</span>
                  {isCertificateRequest && <span className="text-burgundy-700 ml-1">(Required for certificate)</span>}
                </label>
                <input
                  type="text"
                  name="child_first_name"
                  required
                  value={childFirstName}
                  onChange={(e) => setChildFirstName(e.target.value)}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                  placeholder="Enter child's first name"
                />
              </div>

              <div className={isCertificateRequest ? 'md:col-span-2' : ''}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Child Last Name <span className="text-red-500">*</span>
                  {isCertificateRequest && <span className="text-burgundy-700 ml-1">(Required for certificate)</span>}
                </label>
                <input
                  type="text"
                  name="child_last_name"
                  required
                  value={childLastName}
                  onChange={(e) => setChildLastName(e.target.value)}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                  placeholder="Enter child's last name"
                />
              </div>

              {!isCertificateRequest && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date of Birth <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="date_of_birth"
                      required
                      className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sex <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="sex"
                      required
                      className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                    >
                      <option value="">Select sex</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Place of Birth <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="place_of_birth"
                      required
                      className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                      placeholder="City, Province, Country"
                    />
                  </div>
                </>
              )}

              <div className={isCertificateRequest ? 'md:col-span-2' : ''}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {isCertificateRequest ? 'Date of Baptism' : 'Baptism Name'} <span className="text-red-500">*</span>
                  {isCertificateRequest && <span className="text-burgundy-700 ml-1">(Required for certificate)</span>}
                </label>
                {isCertificateRequest ? (
                  <input
                    type="date"
                    name="date_of_baptism"
                    required
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                  />
                ) : (
                  <input
                    type="text"
                    name="baptism_name"
                    required
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                    placeholder="Enter baptism name"
                  />
                )}
              </div>
            </div>
          </div>

          {/* Parent's Information */}
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <i className="ri-parent-line text-gold-600"></i>
              Parent's Information
            </h3>
            
            {/* Father's Information */}
            <div className="mb-6">
              <h4 className="text-base font-medium text-gray-800 mb-4">Father's Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Father's First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="father_first_name"
                    required
                    value={fatherFirstName}
                    onChange={(e) => setFatherFirstName(e.target.value)}
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                    placeholder="Enter father's first name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Father's Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="father_last_name"
                    required
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                    placeholder="Enter father's last name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Father's Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="father_email"
                    required
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                    placeholder="father@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Father's Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="father_phone"
                    required
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                    placeholder="(123) 456-7890"
                  />
                </div>
              </div>
            </div>

            {/* Mother's Information */}
            <div className="mb-6">
              <h4 className="text-base font-medium text-gray-800 mb-4">Mother's Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mother's First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="mother_first_name"
                    required
                    value={motherFirstName}
                    onChange={(e) => setMotherFirstName(e.target.value)}
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                    placeholder="Enter mother's first name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mother's Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="mother_last_name"
                    required
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                    placeholder="Enter mother's last name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mother's Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="mother_email"
                    required
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                    placeholder="mother@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mother's Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="mother_phone"
                    required
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                    placeholder="(123) 456-7890"
                  />
                </div>
              </div>
            </div>

            {/* Parent's Residence */}
            <div>
              <h4 className="text-base font-medium text-gray-800 mb-4">Parent's Residence</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Street Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="residence_street"
                    required
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                    placeholder="Enter street address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="residence_city"
                    required
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                    placeholder="Enter city"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Residence Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="residence_phone"
                    required
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                    placeholder="(123) 456-7890"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Church Event Information */}
          {!isCertificateRequest && (
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
                <i className="ri-church-line text-gold-600"></i>
                Church Event Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="location"
                    required
                    value={locationVal}
                    onChange={(e) => { setLocationVal(e.target.value); if (e.target.value !== 'Other') setLocationOther(''); }}
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                  >
                    <option value="">Select location</option>
                    <option value="Saint Mina & Pope Cyril VI COC">Saint Mina & Pope Cyril VI COC</option>
                    <option value="Other">Other Location</option>
                  </select>
                  {locationVal === 'Other' && (
                    <input
                      type="text"
                      value={locationOther}
                      onChange={(e) => setLocationOther(e.target.value)}
                      placeholder="Please specify the location"
                      className="mt-3 w-full px-4 py-3 text-sm border border-gold-400 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                    />
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Baptism Conducted By <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="conducted_by"
                    required
                    value={conductedBy}
                    onChange={(e) => { setConductedBy(e.target.value); if (e.target.value !== 'Other') setConductedByOther(''); }}
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                  >
                    <option value="">Select priest</option>
                    <option value="Father Anthony">Father Anthony</option>
                    <option value="Other">Other</option>
                  </select>
                  {conductedBy === 'Other' && (
                    <input
                      type="text"
                      value={conductedByOther}
                      onChange={(e) => setConductedByOther(e.target.value)}
                      placeholder="Please specify who will conduct the baptism"
                      className="mt-3 w-full px-4 py-3 text-sm border border-gold-400 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                    />
                  )}
                </div>

                {/* Preferred Date & Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Date
                  </label>
                  <input
                    type="date"
                    name="preferred_date"
                    value={preferredDate}
                    onChange={(e) => setPreferredDate(e.target.value)}
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Time
                  </label>
                  <input
                    type="time"
                    name="preferred_time"
                    value={preferredTime}
                    onChange={(e) => setPreferredTime(e.target.value)}
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                  />
                </div>

                <div className="md:col-span-2">
                  <div className="bg-burgundy-50 border border-burgundy-200 rounded-lg p-4 flex items-start gap-3">
                    <i className="ri-information-line text-burgundy-700 text-xl flex-shrink-0 mt-0.5"></i>
                    <p className="text-sm text-burgundy-800">
                      <strong>Please note:</strong> Nothing is confirmed until Father Anthony has approved the requested date and time, unless it has already been confirmed beforehand. The church office will contact you once your request is reviewed.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Announcement Poster — for new baptism requests only */}
          {!isCertificateRequest && (
            <div className="border-t border-gray-200 pt-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <i className="ri-image-line text-gold-600"></i>
                Baptism Announcement Poster
              </h3>
              <p className="text-sm text-gray-500 mb-2">
                Create a beautiful personalized poster to share with your family and friends!
              </p>
              <div className="bg-gold-50 border border-gold-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                <i className="ri-mail-send-line text-gold-600 text-lg flex-shrink-0 mt-0.5"></i>
                <p className="text-sm text-gray-700">
                  <strong>Your poster will be sent to you by email</strong> along with your booking confirmation. You can then share it with your friends and family to celebrate this blessed occasion.
                </p>
              </div>

              {/* Mode Toggle */}
              <div className="flex gap-4 mb-6">
                <button
                  type="button"
                  onClick={() => setPosterMode('builder')}
                  className={`flex-1 flex items-center justify-center gap-2 px-5 py-4 rounded-lg border-2 transition-all cursor-pointer whitespace-nowrap ${posterMode === 'builder' ? 'border-burgundy-700 bg-burgundy-50 text-burgundy-700 font-semibold' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                >
                  <i className="ri-magic-line text-lg"></i>
                  <span>Use Announcement Builder</span>
                  {posterMode === 'builder' && <i className="ri-check-line text-burgundy-700 ml-1"></i>}
                </button>
                <button
                  type="button"
                  onClick={() => setPosterMode('upload')}
                  className={`flex-1 flex items-center justify-center gap-2 px-5 py-4 rounded-lg border-2 transition-all cursor-pointer whitespace-nowrap ${posterMode === 'upload' ? 'border-burgundy-700 bg-burgundy-50 text-burgundy-700 font-semibold' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                >
                  <i className="ri-upload-cloud-line text-lg"></i>
                  <span>Upload My Own Poster</span>
                  {posterMode === 'upload' && <i className="ri-check-line text-burgundy-700 ml-1"></i>}
                </button>
              </div>

              {posterMode === 'upload' && (
                <div>
                  <p className="text-sm text-gray-600 mb-4">Upload your own custom-designed baptism poster. Accepted formats: JPG, PNG, PDF.</p>
                  <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gold-500 hover:bg-gold-50 transition-colors">
                    {customPosterFile ? (
                      <div className="flex flex-col items-center gap-2">
                        <i className="ri-file-check-line text-4xl text-gold-600"></i>
                        <span className="text-sm font-medium text-burgundy-700">{customPosterFile.name}</span>
                        <span className="text-xs text-gray-500">Click to replace</span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2 text-gray-500">
                        <i className="ri-upload-cloud-2-line text-4xl"></i>
                        <span className="text-sm font-medium">Click to upload your poster</span>
                        <span className="text-xs">JPG, PNG, PDF up to 10MB</span>
                      </div>
                    )}
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf"
                      className="hidden"
                      onChange={(e) => setCustomPosterFile(e.target.files?.[0] ?? null)}
                    />
                  </label>
                </div>
              )}
            </div>
          )}

          {/* Announcement Builder (builder mode, new baptism only) */}
          {!isCertificateRequest && posterMode === 'builder' && (
            <AnnouncementBuilder
              type="baptism"
              name1={childFirstName}
              familyName1={childLastName}
              name2={fatherFirstName && motherFirstName ? `${fatherFirstName} & ${motherFirstName}` : fatherFirstName || motherFirstName}
              baptismDate={preferredDate}
              eventLocation={locationVal === 'Other' ? locationOther : locationVal}
              onChange={setBuilderData}
            />
          )}

          {/* Service Acknowledgments */}
          {!isCertificateRequest && (
            <div className="bg-burgundy-50 border border-burgundy-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-information-line text-burgundy-700"></i>
                Important Information
              </h3>
              <div className="space-y-3 text-sm text-gray-700">
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-gold-600 mt-0.5 flex-shrink-0"></i>
                  <p>
                    <strong>Birth certificate and newsletter:</strong> These are mandatory and will be provided after the baptism ceremony.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-gold-600 mt-0.5 flex-shrink-0"></i>
                  <p>
                    <strong>Privacy notice:</strong> Baptism information will only be published after the event. The service will not be broadcasted live, and no posts on social media will be published beforehand.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-gold-600 mt-0.5 flex-shrink-0"></i>
                  <p>
                    <strong>Certificate:</strong> You will receive a baptism certificate, and the church will congratulate you through the church newsletter.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Submit Status Messages */}
          {submitStatus === 'success' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
              <i className="ri-checkbox-circle-fill text-green-600 text-xl flex-shrink-0"></i>
              <div>
                <p className="text-green-800 font-medium">Form submitted successfully!</p>
                <p className="text-green-700 text-sm mt-1">
                  Thank you for your submission. The church office will contact you shortly to confirm the details. {!isCertificateRequest && 'Your personalized announcement poster will be sent to your email with the confirmation.'}
                </p>
              </div>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <i className="ri-error-warning-fill text-red-600 text-xl flex-shrink-0"></i>
              <div>
                <p className="text-red-800 font-medium">Submission failed</p>
                <p className="text-red-700 text-sm mt-1">
                  Please check your connection and try again. If the problem persists, contact the church office.
                </p>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex gap-4 pt-6">
            <button
              type="button"
              onClick={() => navigate('/resources')}
              className="px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 text-sm font-medium text-white bg-burgundy-700 rounded-lg hover:bg-burgundy-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Submitting...
                </span>
              ) : (
                'Submit Baptism Form'
              )}
            </button>
          </div>
        </form>
      </div>
    </PageLayout>
  );
}
